import { formatDuration } from '../lib/format'
import type { Block, Category } from '../data/types'

export interface CategoryTotal {
  category: Category
  ms: number
}

export interface Totalled {
  /** What counts towards the tracked figure, longest first. */
  totals: CategoryTotal[]
  /** Time on excluded categories, kept visible but out of every sum. */
  uncounted: CategoryTotal[]
  tracked: number
}

/**
 * Time per category inside a range. Blocks are clipped to the range so a session
 * spanning the boundary counts only the part that falls inside it.
 *
 * Excluded categories are separated rather than dropped: an hour of lunch is
 * still an hour you logged and want to see, it just is not an hour of work, so
 * it stays visible and stays out of `tracked`.
 */
export function totalsFor(
  blocks: Block[],
  categories: Map<string, Category>,
  from: number,
  to: number,
  excluded: Set<string> = new Set(),
  now: number = Date.now(),
): Totalled {
  const byCategory = new Map<string, number>()
  let tracked = 0

  for (const block of blocks) {
    const start = Math.max(block.start, from)
    const end = Math.min(block.end ?? now, to)
    const ms = end - start
    if (ms <= 0) continue
    byCategory.set(block.categoryId, (byCategory.get(block.categoryId) ?? 0) + ms)
    if (!excluded.has(block.categoryId)) tracked += ms
  }

  const totals: CategoryTotal[] = []
  const uncounted: CategoryTotal[] = []
  for (const [categoryId, ms] of byCategory) {
    const category = categories.get(categoryId)
    if (!category) continue
    ;(excluded.has(categoryId) ? uncounted : totals).push({ category, ms })
  }
  const longestFirst = (a: CategoryTotal, b: CategoryTotal) => b.ms - a.ms
  totals.sort(longestFirst)
  uncounted.sort(longestFirst)
  return { totals, uncounted, tracked }
}

interface Props {
  blocks: Block[]
  categories: Map<string, Category>
  from: number
  to: number
  /** Categories whose time is shown but never summed. */
  excluded?: Set<string>
  /** Same number for the previous period, when a comparison is meaningful. */
  comparison?: { tracked: number; label: string }
}

export function Totals({ blocks, categories, from, to, excluded, comparison }: Props) {
  const { totals, uncounted, tracked } = totalsFor(blocks, categories, from, to, excluded)
  if (totals.length === 0 && uncounted.length === 0) {
    return (
      <p className="px-1 py-2 text-[13px] text-faint">
        Nothing tracked yet. Pick a category above and drag across the timeline.
      </p>
    )
  }

  const delta = comparison ? tracked - comparison.tracked : null

  return (
    <div className="flex flex-col gap-2">
      {tracked > 0 ? (
        <div className="flex h-1.5 overflow-hidden rounded-full bg-line">
          {totals.map(({ category, ms }) => (
            <span
              key={category.id}
              title={`${category.label} ${formatDuration(ms)}`}
              style={{ width: `${(ms / tracked) * 100}%`, backgroundColor: category.color }}
            />
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-[13px] font-medium text-text tnum">
          {formatDuration(tracked)} <span className="font-normal text-faint">tracked</span>
        </span>
        {delta !== null && comparison ? (
          <span className="text-[12px] text-faint tnum">
            {delta >= 0 ? '+' : '−'}
            {formatDuration(Math.abs(delta))} vs {comparison.label}
          </span>
        ) : null}
        {totals.map(({ category, ms }) => (
          <span key={category.id} className="flex items-baseline gap-1.5 text-[12px] text-dim">
            <span
              className="size-2 shrink-0 translate-y-px rounded-[3px]"
              style={{ backgroundColor: category.color }}
              aria-hidden="true"
            />
            {category.shortLabel}
            <span className="text-faint tnum">{formatDuration(ms)}</span>
          </span>
        ))}
        {uncounted.map(({ category, ms }) => (
          <span
            key={category.id}
            title={`${category.label} is not counted in any figure`}
            className="flex items-baseline gap-1.5 text-[12px] text-faint line-through decoration-faint/50"
          >
            <span
              className="size-2 shrink-0 translate-y-px rounded-[3px] opacity-40"
              style={{ backgroundColor: category.color }}
              aria-hidden="true"
            />
            {category.shortLabel}
            <span className="tnum">{formatDuration(ms)}</span>
          </span>
        ))}
      </div>
    </div>
  )
}
